/*    
<summary>
  This functional component "InactivityPopup" provides popup 
  Developer:Aashish Singh, Created Date:10-July-2024
</summary>
<returns>Returns JSX</returns>
*/
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { observer } from 'mobx-react';
import { formatMessage } from '../../../translations/format-message';
import toast from 'react-hot-toast';
import RoutesConstants from '../../Routes/routes.constants';
import { useStore } from '../../../contexts/StoreProvider';
import { useHistory } from 'react-router-dom';
import { idleLogoutTime } from '../../../constants/common-constants';

const InactivityPopup: React.FC = () => {
  const { authStore, userStore, tenantStore } = useStore();
  const { logout, logoutError, logoutSuccess, resetLogout } = authStore;
  const timeoutRef = useRef<number | undefined>(undefined);
  const history = useHistory();
  const [isLoggedOutByInactivity, setIsLoggedOutByInactivity] = useState<boolean>(false); 

  const resetStores = () => {
    authStore.reset();
    tenantStore.resetStore();
    userStore.reset();
  };

  const handleActivity = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = window.setTimeout(() => {
      setIsLoggedOutByInactivity(true);
      logout(true);
    }, idleLogoutTime);
  }, [logout]);

  useEffect(() => {
    const events: string[] = [
      'mousemove',
      'keydown',
      'mousedown',
      'touchstart',
      'click',
      'scroll',
      'wheel',
      'resize',
      'focus',
      'dblclick',
      'touchmove',
      'touchend',
      'touchcancel',
    ];

    events.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    handleActivity();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [handleActivity]);

  useEffect(() => {
    if (logoutSuccess && isLoggedOutByInactivity) {
      toast.success(formatMessage('session_expired'));
      resetStores();
      resetLogout();
      setIsLoggedOutByInactivity(false);
      history.push(RoutesConstants.Login);
    }
  }, [logoutSuccess, history, resetStores, resetLogout]);

  useEffect(() => {
    if (logoutError) {
      resetLogout();
    }
  }, [logoutError, resetLogout]);

  return null; // No UI needed
};

export default observer(InactivityPopup);
